import Image from 'next/image'
import {FC, ReactNode} from 'react'

import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {CloseIcon} from '@/components/common/icons/CloseIcon'
import {usePreferences} from '@/components/context/Preferences'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {USER_TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {getDefaultLawyerByCountry, getSignatureSize} from '@/utils/team/helpers'

type ReassuranceBannerProps = {
  description: string | ReactNode
  onClose?: () => void
}

export const ReassuranceBanner: FC<ReassuranceBannerProps> = ({description, onClose}) => {
  const {t} = useTranslation()

  const {destination} = usePreferences()
  const lawyer = getDefaultLawyerByCountry(destination)

  const {name, role, photoSrc, photoAlt, signature} = lawyer

  return (
    <>
      <div className="banner">
        <div className="wrapper author">
          <div className="wrapper">
            <div className="photo-wrapper">
              <Image
                src={photoSrc}
                alt={photoAlt}
                style={{objectFit: 'contain'}}
                width={48}
                height={48}
              />
            </div>
            <div>
              <Text fontWeight={TextFontWeight.bold}>{t(name)}</Text>
              <Text variant={TextVariant.text14} color={theme.palette.text.light}>
                {t(role)}
              </Text>
            </div>
          </div>
          {signature && (
            <div className="signature">
              <Image
                src={signature.src}
                alt={signature.alt}
                {...getSignatureSize(signature, 40)}
                style={{display: 'block'}}
              />
            </div>
          )}
        </div>
        <div className="description">{description}</div>
        {onClose && (
          <button className="close-icon" type="button" onClick={onClose}>
            <CloseIcon />
          </button>
        )}
      </div>

      <style jsx>{`
        .banner {
          position: relative;
          padding: ${theme.spacing[4]};
          border-radius: 12px;
          background-color: ${theme.palette.background.butter};
          box-shadow: inset 0px 0px 0px 1px ${theme.palette.butter[400]};
        }
        .wrapper {
          display: flex;
          align-items: center;
          width: 100%;
        }
        .author {
          justify-content: space-between;
        }
        .photo-wrapper {
          position: relative;
          width: 48px;
          height: 48px;
          flex: 0 0 48px;
          margin-right: ${theme.spacing[2.5]};
          border-radius: 8px;
          overflow: hidden;
        }
        .description {
          margin-top: ${theme.spacing[3]};
        }
        .close-icon {
          position: absolute;
          right: 6px;
          top: 6px;
          padding: ${theme.spacing[2]};
        }
        .signature {
          margin: 0 ${theme.spacing[2]};
        }
        @media ${USER_TABLET_SCREENSIZE} {
          .signature {
            display: none;
          }
        }
      `}</style>
    </>
  )
}
